import { Fragment, type ReactNode } from 'react';

/**
 * Matches bare URLs and `www.` hosts in plain message text.
 *
 * Only `http` and `https` are linked. A permissive scheme match would happily
 * turn `javascript:` and `data:` into working anchors, which is how a chat
 * message becomes a delivery mechanism — and this thread's contents come from
 * another user by definition.
 *
 * Trailing punctuation is deliberately excluded from the match, so
 * "see https://example.gov/notice." links the URL and leaves the full stop as
 * prose. Balanced trailing brackets are handled below rather than here, because
 * a regex that tracks nesting is worse than a short loop that does.
 */
const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;

/** Characters that end a sentence far more often than they end a URL. */
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;

/**
 * Splits text into runs, linking any URL it finds.
 *
 * Returns nodes rather than HTML: the alternative is `dangerouslySetInnerHTML`
 * over text another user wrote, which is the one thing a message renderer must
 * never do. React escapes every text run here for free.
 */
export function linkify(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // `exec` in a loop needs a fresh lastIndex; the literal is module-scoped and
  // stateful because of the `g` flag.
  URL_PATTERN.lastIndex = 0;

  while ((match = URL_PATTERN.exec(text)) !== null) {
    const raw = match[0];
    const trimmed = trimTrailing(raw);
    const start = match.index;

    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));

    nodes.push(
      <a
        key={`${start}-${trimmed}`}
        href={hrefFor(trimmed)}
        target="_blank"
        // `noopener` denies the opened page a handle back to this one;
        // `noreferrer` keeps the portal's URL — which contains file ids — out of
        // the destination's referrer log.
        rel="noopener noreferrer nofollow"
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        {trimmed}
      </a>,
    );

    // Anything trimmed off the match is prose, and has to go back.
    lastIndex = start + trimmed.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));

  return nodes.map((node, index) =>
    typeof node === 'string' ? <Fragment key={`t-${index}`}>{node}</Fragment> : node,
  );
}

/**
 * Drops trailing punctuation, and a closing bracket the URL never opened.
 *
 * "(see https://example.gov/a)" should link `https://example.gov/a`, while
 * "https://en.wikipedia.org/wiki/Foo_(bar)" keeps its balanced pair.
 */
function trimTrailing(url: string): string {
  let out = url.replace(TRAILING_PUNCTUATION, '');
  while (out.endsWith(')') && countOf(out, ')') > countOf(out, '(')) {
    out = out.slice(0, -1).replace(TRAILING_PUNCTUATION, '');
  }
  return out;
}

function countOf(value: string, character: string): number {
  let total = 0;
  for (const c of value) if (c === character) total += 1;
  return total;
}

/** `www.example.gov` is a host, not a URL; without a scheme it resolves relatively. */
function hrefFor(url: string): string {
  return url.toLowerCase().startsWith('www.') ? `https://${url}` : url;
}
